"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_DOCUMENT_SOURCES_META = void 0;
exports.appDocumentBaseHref = appDocumentBaseHref;
exports.appDocumentStamp = appDocumentStamp;
exports.appDocumentBridgeScript = appDocumentBridgeScript;
exports.appDocumentDataTicket = appDocumentDataTicket;
exports.appDocumentSources = appDocumentSources;
exports.appDocumentScriptCors = appDocumentScriptCors;
exports.composeAppPageDocument = composeAppPageDocument;
exports.finishAppPlatformDocument = finishAppPlatformDocument;
const app_base_contract_1 = require("./app-base-contract");
const app_base_frame_1 = require("./app-base-frame");
const app_base_html_document_1 = require("./app-base-html-document");
const app_base_route_navigation_1 = require("./app-base-route-navigation");
const app_base_theme_tokens_1 = require("./app-base-theme-tokens");
const app_page_access_1 = require("./app-page-access");
const html_escape_1 = require("./html-escape");
/** The one `<base>` of a page: the directory of its file under the App. */
function appDocumentBaseHref(html, basePath, artifactPath) {
    const withoutBase = html.replace(/<base\b[^>]*>/gi, '');
    const directory = artifactPath
        .split('/')
        .slice(0, -1)
        .map(segment => encodeURIComponent(segment))
        .join('/');
    return (0, app_base_html_document_1.insertIntoAppHead)(withoutBase, `<base href="${basePath}${directory ? `/${directory}` : ''}/">`);
}
/**
 * Pages change without a new document only between documents of one App in
 * one delivered state: Apps of an App Container never share a window.
 */
function appDocumentStamp(html, appBaseId, snapshotVersion, mountId) {
    const stamp = (0, html_escape_1.escapeHtml)([appBaseId, snapshotVersion, ...(mountId === null ? [] : [mountId])].join('.'));
    return (0, app_base_html_document_1.setAppHtmlAttribute)(html, app_base_route_navigation_1.APP_BASE_DOCUMENT_ATTRIBUTE, stamp);
}
/**
 * The one Bridge script of a page, from the App's own address. A page the
 * workspace embeds has no session of its address; its Bridge is fetched
 * with the ticket the page was opened with.
 */
function appDocumentBridgeScript(html, basePath, workspaceTicket) {
    const withoutExisting = html.replace(/<script\b[^>]*\bsrc\s*=\s*["'][^"']*\/_app-bridge\.js["'][^>]*>\s*<\/script>/gi, '');
    return (0, app_base_html_document_1.insertIntoAppHead)(withoutExisting, `<script src="${(0, html_escape_1.escapeHtml)(`${basePath}/_app-bridge.js${workspaceTicket ? `?token=${encodeURIComponent(workspaceTicket)}` : ''}`)}" defer></script>`);
}
/** The data ticket of a page; empty, the Bridge fetches one before its first read. */
function appDocumentDataTicket(html, token) {
    const withoutExisting = html.replace(/<meta\b[^>]*\bname\s*=\s*["']streamlain-custom-data-token["'][^>]*>/gi, '');
    return (0, app_base_html_document_1.insertIntoAppHead)(withoutExisting, `<meta name="streamlain-custom-data-token" content="${(0, html_escape_1.escapeHtml)(token)}">`);
}
exports.APP_DOCUMENT_SOURCES_META = 'streamlain-app-sources';
/**
 * The sources of a page with the columns its viewer reads, admitted with its
 * data ticket; empty, they come with the ticket the Bridge fetches.
 */
function appDocumentSources(html, sources) {
    const withoutExisting = html.replace(new RegExp(`<meta\\b[^>]*\\bname\\s*=\\s*["']${exports.APP_DOCUMENT_SOURCES_META}["'][^>]*>`, 'gi'), '');
    return (0, app_base_html_document_1.insertIntoAppHead)(withoutExisting, `<meta name="${exports.APP_DOCUMENT_SOURCES_META}" content="${(0, html_escape_1.escapeHtml)(sources ? JSON.stringify(sources) : '')}">`);
}
/** Script requests of a sandboxed page go without credentials. */
function appDocumentScriptCors(html) {
    return html.replace(/<script\b([^>]*\bsrc\s*=\s*["'][^"']+["'][^>]*)>/gi, (script, attributes) => {
        if (/\bcrossorigin(?:\s*=|\s|$)/i.test(attributes))
            return script;
        return `<script crossorigin="anonymous"${attributes}>`;
    });
}
/**
 * The page of a route with the frame of its App around it (only the entry of
 * a route of an App with a frame) and the base of its address.
 */
async function composeAppPageDocument(input) {
    const { artifact, route, context } = input;
    const { frame, manifest } = artifact;
    let html = input.pageHtml;
    if (route &&
        input.filePath === route.entry &&
        (0, app_base_contract_1.appBaseHasFrame)({ frame, ...manifest })) {
        const topBar = (0, app_page_access_1.appTopBarOpenTo)(manifest.topBar, manifest.appDeliveries, route.path, context.pageAccess, context.viewer);
        const content = await (0, app_base_frame_1.readAppBaseFrameContent)({
            frame,
            topBar,
            name: manifest.name,
            locale: context.locale,
            readText: input.readText,
        });
        html = (0, app_base_frame_1.composeAppBaseFrameDocument)({
            pageHtml: html,
            pagePath: input.filePath,
            ...content,
            topBar,
            layout: manifest.navigationLayout,
            navigation: (0, app_page_access_1.appNavigationOpenTo)(manifest.navigation, context.pageAccess, context.viewer),
            currentRoutePath: route.path,
            locale: context.locale,
            routeHref: routePath => routePath === '/'
                ? `${context.basePath}/`
                : `${context.basePath}${routePath}`,
            parseDocument: input.parseDocument,
        });
    }
    return appDocumentBaseHref(html, context.basePath, input.filePath);
}
/**
 * The platform marks of a delivered page: the delivered state, the design of
 * its App, the Bridge, and (in a flow run of the author) the flow observer,
 * every script without credentials.
 */
function finishAppPlatformDocument(html, input) {
    const { context } = input;
    const mount = input.route
        ? (0, app_base_contract_1.mountOfRoute)(context.mounts, input.route.path)
        : null;
    let page = appDocumentStamp(html, context.appBaseId, context.snapshotVersion, mount?.mountId ?? null);
    page = (0, app_base_theme_tokens_1.applyAppBaseDesign)(page, (0, app_base_contract_1.appBaseDesignOf)(mount));
    page = appDocumentBridgeScript(page, context.basePath, input.workspaceTicket);
    if (context.installable)
        page = (0, app_base_html_document_1.insertIntoAppHead)(page.replace(/<link\b[^>]*\brel\s*=\s*["']manifest["'][^>]*>/gi, ''), `<link rel="manifest" href="${app_base_contract_1.APP_BASE_WEB_MANIFEST_ROUTE}">`);
    if (input.flowRuntime)
        page = (0, app_base_html_document_1.insertIntoAppHead)(page, `<script src="${(0, html_escape_1.escapeHtml)(`${context.basePath}/_app-flow-runtime.js`)}" defer></script>`);
    return appDocumentScriptCors(page);
}
